import type { LucideIcon } from "lucide-react";
import {
    FileSearch,
    ShieldAlert,
    Calculator,
    Landmark,
    Fingerprint,
    SearchCheck,
    ClipboardCheck,
    Route,
    Radar,
    Gavel,
    Cpu,
    Layers,
    Lock,
    Compass,
    FileCheck2,
    ShieldCheck,
    Scale,
    Briefcase,
    FlaskConical,
    Building2,
    Banknote,
    Umbrella,
    TrendingUp,
    Rocket,
    FileText,
} from "lucide-react";

export interface ServiceGroup {
    label?: string;
    items: string[];
}

export interface Service {
    id: string;
    title: string;
    short: string;
    icon: LucideIcon;
    tagline?: string;
    paragraphs?: string[];
    groups?: ServiceGroup[];
    outro?: string;
    note?: string;
}

export const services: Service[] = [
    {
        id: "forensic-fraud-investigations",
        title: "Forensic & Fraud Investigations",
        short: "Investigation of suspected financial, corporate and business fraud, misconduct and irregularities.",
        icon: FileSearch,
        paragraphs: [
            "We investigate suspected financial, corporate and business fraud and misconduct.",
        ],
        groups: [
            {
                label: "Our work may involve matters relating to",
                items: [
                    "Employee and management fraud",
                    "Procurement and vendor fraud",
                    "Accounting and financial statement irregularities",
                    "Embezzlement and misappropriation",
                    "Asset diversion",
                    "Bribery and corruption concerns",
                    "Conflicts of interest",
                    "Revenue and expense irregularities",
                    "Transaction fraud",
                    "Whistleblower allegations",
                    "Corporate misconduct",
                    "Other complex financial and business irregularities",
                ],
            },
        ],
    },
    {
        id: "whistleblower-investigations",
        title: "Whistleblower Investigations",
        short: "Independent examination of allegations involving fraud, misconduct, conflicts of interest, procurement concerns and financial irregularities.",
        icon: ShieldAlert,
        tagline: "Allegations require facts, not assumptions.",
        paragraphs: [
            "Whistleblower complaints can involve sensitive allegations concerning employees, management, vendors, related parties and business processes.",
            "We assist organisations in independently examining allegations and establishing the underlying facts through financial, documentary, digital and investigative analysis.",
        ],
        groups: [
            {
                label: "Matters may involve",
                items: [
                    "Financial misconduct",
                    "Procurement irregularities",
                    "Employee or management misconduct",
                    "Conflicts of interest",
                    "Undisclosed related-party relationships",
                    "Vendor-related concerns",
                    "Misappropriation of funds or assets",
                    "Manipulation of records",
                    "Policy violations",
                    "Bribery or corruption concerns",
                    "Other internal irregularities",
                ],
            },
        ],
        outro:
            "Our approach may include reviewing the allegation; identifying relevant evidence; analysing transactions and documents; examining communications and digital data where relevant; testing relationships; and corroborating observations through available independent information.",
        note: "The objective is to determine what the available evidence supports and present findings in a clear, objective and evidence-based manner.",
    },
    {
        id: "financial-investigation-forensic-accounting",
        title: "Financial Investigation & Forensic Accounting",
        short: "Analysis of financial records, transactions and fund movements to identify discrepancies, unusual patterns and financial linkages.",
        icon: Calculator,
        paragraphs: [
            "We analyse financial statements, books of account, ledgers, bank transactions and other financial data to identify:",
        ],
        groups: [
            {
                items: [
                    "Financial discrepancies",
                    "Unusual transaction patterns",
                    "Concealed transactions",
                    "Related-party dealings",
                    "Fund movements",
                    "Financial linkages",
                    "Transaction anomalies",
                ],
            },
            {
                label: "Services",
                items: [
                    "Transaction Analysis",
                    "Fund-Flow Analysis",
                    "Financial Reconstruction",
                    "Forensic Accounting",
                ],
            },
        ],
        note: "The objective is not merely to identify unusual transactions but to understand the financial story behind them.",
    },
    {
        id: "bank-statement-investigations",
        title: "Bank Statement & Financial Document Investigations",
        short: "Examination of bank statements and financial documents for inconsistencies, alterations, transaction anomalies and indicators requiring investigation.",
        icon: Landmark,
        tagline: "When financial records themselves are in question.",
        paragraphs: [
            "Bank statements and financial documents can become important evidence in fraud investigations, litigation, disputes and other investigative matters.",
            "We undertake analytical examination to identify inconsistencies, transaction anomalies and indicators that may require further investigation.",
        ],
        groups: [
            {
                label: "Examination scope",
                items: [
                    "Transaction sequence and chronology",
                    "Opening and closing balance consistency",
                    "Debit and credit movements",
                    "Running balance calculations",
                    "Unusual or unexplained transactions",
                    "Transaction narration patterns",
                    "Formatting and structural inconsistencies",
                    "Missing or duplicated entries",
                    "Date and amount inconsistencies",
                    "Potential signs of alteration or manipulation",
                    "Cross-verification against other available financial records",
                    "Comparison with accounting records, supporting documents or independently available information",
                ],
            },
            {
                label: "Typical matters",
                items: [
                    "Suspected forged or altered bank statements",
                    "Court or dispute-related financial records",
                    "Fraud investigations",
                    "Employee investigations",
                    "Transaction disputes",
                    "Loan or financial misrepresentation concerns",
                    "Source-of-funds enquiries",
                    "Fund-flow investigations",
                ],
            },
        ],
        outro:
            "Where digitally available material is provided, the examination may also consider relevant file properties, metadata and other technical information depending upon the nature and quality of the source material.",
        note: "Financial records should not only be read. They should reconcile.",
    },
    {
        id: "digital-forensics-technical-investigation",
        title: "Digital Forensics & Technical Investigation",
        short: "Identification, preservation, examination and analysis of digital evidence relevant to investigative matters.",
        icon: Fingerprint,
        tagline: "Evidence doesn't always exist on paper.",
        paragraphs: [
            "Digital evidence can provide information that may not be visible from financial records alone.",
        ],
        groups: [
            {
                label: "Digital sources",
                items: [
                    "Computers and laptops",
                    "Mobile devices",
                    "Servers",
                    "Storage media",
                    "Email systems",
                    "Digital files",
                    "Electronic communications",
                    "Other relevant digital sources",
                ],
            },
            {
                label: "Digital artefacts",
                items: [
                    "Relevant user activity",
                    "Communication patterns",
                    "File access and movement",
                    "Document metadata",
                    "Deleted or altered information",
                    "External storage activity",
                    "Email and cloud activity",
                    "Potential movement of information",
                    "Timelines relevant to the investigation",
                ],
            },
            {
                label: "Investigation areas",
                items: [
                    "Fraud",
                    "Financial investigations",
                    "Whistleblower investigations",
                    "Data theft",
                    "Employee misconduct",
                    "Intellectual property matters",
                    "Cyber-enabled fraud",
                    "Digital evidence review",
                ],
            },
        ],
        outro:
            "Digital evidence is examined in the context of the investigative questions being addressed and, where relevant, corroborated with financial, documentary and other available information.",
        note: "When the trail moves from the ledger to the device, our investigation follows it.",
    },
    {
        id: "fraud-risk-corporate-investigations",
        title: "Fraud Risk & Corporate Investigations",
        short: "Assessment of fraud exposure, internal controls and vulnerabilities together with investigation of internal irregularities and misconduct.",
        icon: SearchCheck,
        paragraphs: [
            "We assist organisations in understanding fraud exposure, internal controls, business processes and areas of vulnerability.",
        ],
        groups: [
            {
                label: "Our work may include investigations and reviews relating to",
                items: [
                    "Whistleblower allegations",
                    "Employee misconduct",
                    "Procurement concerns",
                    "Conflicts of interest",
                    "Related-party matters",
                    "Internal irregularities",
                    "Control weaknesses",
                ],
            },
        ],
        note: "The objective is to understand both the incident and the underlying circumstances that may have allowed it to occur.",
    },
    {
        id: "due-diligence-investigative-intelligence",
        title: "Due Diligence & Investigative Intelligence",
        short: "Financial, corporate and investigative due diligence supported by research, relationship mapping and evidence-based verification.",
        icon: ClipboardCheck,
        paragraphs: [
            "We undertake financial, corporate and investigative due diligence supported by research, analysis and evidence-based verification.",
        ],
        groups: [
            {
                label: "Our work may involve",
                items: [
                    "Relationship mapping",
                    "Ownership analysis",
                    "Background assessment",
                    "Integrity-risk analysis",
                    "Corporate research",
                    "Investigative intelligence",
                    "Evidence-based verification",
                ],
            },
        ],
        note: "The purpose is to look beyond information presented at face value and develop a more complete understanding of relevant individuals, entities, relationships and risks.",
    },
    {
        id: "asset-fund-tracing",
        title: "Asset & Fund Tracing",
        short: "Tracing funds, assets, ownership interests, beneficial interests and financial relationships.",
        icon: Route,
        tagline: "Follow the money.",
        paragraphs: [
            "We trace funds, assets, ownership interests and financial connections.",
            "Our work may involve identifying assets or funds that have been transferred, diverted or held through complex ownership structures.",
        ],
        groups: [
            {
                label: "Assignments may combine",
                items: [
                    "Financial analysis",
                    "Bank transaction analysis",
                    "Transaction tracing",
                    "Corporate research",
                    "Ownership analysis",
                    "Investigative intelligence",
                    "Relationship mapping",
                ],
            },
            {
                label: "Focus",
                items: [
                    "Funds",
                    "Assets",
                    "Beneficial Interests",
                    "Ownership Structures",
                    "Financial Linkages",
                ],
            },
        ],
    },
    {
        id: "market-corporate-intelligence",
        title: "Market & Corporate Intelligence",
        short: "Discreet corporate research and analysis of relationships, ownership linkages, integrity risks and relevant business intelligence.",
        icon: Radar,
        paragraphs: [
            "We undertake discreet research and analysis designed to identify information relevant to business and investigative decisions.",
        ],
        groups: [
            {
                label: "Our work may include analysis of",
                items: [
                    "Undisclosed or non-obvious relationships",
                    "Corporate linkages",
                    "Conflicts of interest",
                    "Ownership relationships",
                    "Integrity risks",
                    "Unusual financial or business patterns",
                    "Relevant background information",
                ],
            },
        ],
        note: "The objective is to develop evidence-based intelligence rather than rely upon assumptions or unverified information.",
    },
    {
        id: "litigation-investigation-support",
        title: "Litigation & Investigation Support",
        short: "Financial, digital and investigative analysis to organise complex evidence, transactions and findings for disputes and investigations.",
        icon: Gavel,
        paragraphs: [
            "We provide financial and investigative support in complex matters involving:",
        ],
        groups: [
            {
                items: [
                    "Financial reconstruction",
                    "Bank statement analysis",
                    "Transaction analysis",
                    "Evidence review",
                    "Asset tracing",
                    "Digital evidence",
                    "Investigative intelligence",
                    "Preparation of analytical findings",
                ],
            },
        ],
        outro:
            "Our role is to help organise and analyse complex information so that relevant facts, transactions and evidence can be clearly understood.",
        note: "For confidentiality reasons, client names and sensitive matter details are not disclosed unless appropriate authorisation is available.",
    },
];

export interface Triad {
    title: string;
    description: string;
}

export const triad: Triad[] = [
    {
        title: "Investigate",
        description:
            "Suspected fraud, whistleblower allegations, employee or management misconduct, financial irregularities, procurement concerns and other complex investigative matters.",
    },
    {
        title: "Analyse",
        description:
            "Financial records, bank statements, transactions, business data, communications, documents and digital information to identify patterns, discrepancies and relationships.",
    },
    {
        title: "Trace",
        description:
            "Funds, assets, entities, ownership structures and financial connections to understand where the evidence leads.",
    },
];

export const selectedExpertise: string[] = [
    "Forensic & fraud investigations",
    "Whistleblower investigations",
    "Financial investigation & forensic accounting",
    "Bank statement & financial document investigations",
];

export const connectionChain: string[] = [
    "A transaction may lead to an account.",
    "An account may lead to an entity.",
    "An entity may lead to a relationship.",
    "A relationship may lead to a communication.",
    "A communication may lead to a digital trail.",
];

export const connectionFormula =
    "Financial Intelligence + Forensic Investigation + Digital Evidence + Technical Expertise";

export const connectionDigitalNote =
    "Evidence doesn't always exist on paper. Computers. Mobile devices. Emails. Digital files. Storage media. Electronic communications.";

export const digitalEvidenceHome = {
    heading: "Evidence doesn't always exist on paper",
    paragraphs: [
        "Modern investigations increasingly require digital information to be examined alongside financial and documentary evidence.",
        "Our digital forensic capabilities support the identification, preservation, examination and analysis of digital evidence relevant to fraud investigations, whistleblower matters, financial investigations, employee misconduct, data theft, intellectual property matters and other investigative concerns.",
        "Digital evidence may help identify user activity, communication patterns, file movements, document metadata, deleted information and other artefacts relevant to reconstructing events.",
    ],
};

export interface MoneyGroup {
    title: string;
    description: string;
}

export const followTheMoney = {
    heading: "Follow the money. Reconstruct the relationships.",
    lead: "Financial transactions often reveal relationships that are not immediately visible.",
    body: "We analyse financial and business information to identify patterns, discrepancies, financial trails and relationships that help establish the facts.",
    groups: [
        {
            title: "Transactions",
            description:
                "Bank transactions, bank statements, ledgers, payments, receipts and unusual activity.",
        },
        {
            title: "Entities",
            description: "Companies, individuals, related parties and ownership structures.",
        },
        {
            title: "Data",
            description:
                "Financial statements, accounting records, financial information, documents and relevant datasets.",
        },
        {
            title: "Analysis",
            description:
                "Fund flows, transaction patterns, relationships and financial reconstruction.",
        },
    ] as MoneyGroup[],
};

export interface Pillar {
    title: string;
    description: string;
    icon: LucideIcon;
}

export const whyInfrasic: Pillar[] = [
    {
        title: "Investigative Expertise",
        description:
            "We specialise in understanding complex financial, corporate and investigative matters.",
        icon: FileSearch,
    },
    {
        title: "Multidisciplinary Capability",
        description:
            "Financial, investigative, legal and technical perspectives can be brought together depending on the requirements of the matter.",
        icon: Layers,
    },
    {
        title: "Financial + Digital",
        description:
            "We examine financial information and digital evidence where the investigation requires both.",
        icon: Cpu,
    },
    {
        title: "Evidence-Based",
        description:
            "Our approach is centred around facts, documentation, analysis and corroboration.",
        icon: FileCheck2,
    },
    {
        title: "Confidential",
        description:
            "Sensitive investigative matters are handled with appropriate discretion and professional care.",
        icon: Lock,
    },
    {
        title: "Actionable",
        description:
            "We convert complex information into clear findings and practical insights.",
        icon: Compass,
    },
    {
        title: "End-to-End Support",
        description:
            "From initial allegation assessment and evidence identification through investigation, analysis, tracing, corroboration and reporting.",
        icon: ShieldCheck,
    },
];

export interface ApproachStep {
    number: string;
    title: string;
    description: string;
}

export const approachSteps: ApproachStep[] = [
    {
        number: "01",
        title: "Understand",
        description:
            "Understand the allegation, background, business environment, circumstances and investigative objectives.",
    },
    {
        number: "02",
        title: "Identify",
        description:
            "Identify relevant financial, documentary, digital and other available sources of evidence.",
    },
    {
        number: "03",
        title: "Analyse",
        description:
            "Analyse transactions, financial records, bank statements, relationships, communications, documents and relevant data.",
    },
    {
        number: "04",
        title: "Trace",
        description:
            "Follow funds, assets, transactions, entities and connections wherever the available evidence leads.",
    },
    {
        number: "05",
        title: "Corroborate",
        description:
            "Validate observations through supporting documentation, independent information and cross-analysis of available evidence. Where information cannot be independently confirmed, the limitation is identified appropriately.",
    },
    {
        number: "06",
        title: "Report",
        description:
            "Present clear findings, supporting evidence and relevant investigative insights. Our reports distinguish factual findings, analytical observations and matters requiring further verification where appropriate.",
    },
];

export const principles: Pillar[] = [
    {
        title: "Evidence Before Assumption",
        description: "Allegations are examined through available facts, records, data and evidence.",
        icon: FileCheck2,
    },
    {
        title: "Objectivity",
        description:
            "The investigation seeks to establish what the available evidence supports—not confirm a predetermined conclusion.",
        icon: Scale,
    },
    {
        title: "Corroboration",
        description:
            "Material observations are evaluated against supporting information wherever available.",
        icon: ShieldCheck,
    },
    {
        title: "Confidentiality",
        description:
            "Sensitive information, particularly whistleblower and employee matters, is handled with appropriate discretion.",
        icon: Lock,
    },
    {
        title: "Independence of Analysis",
        description:
            "Financial, documentary and digital information is assessed on its own evidentiary value and alongside other available information.",
        icon: Compass,
    },
    {
        title: "Clarity",
        description:
            "Complex information is organised and communicated through clear, evidence-based findings.",
        icon: FileText,
    },
];

export interface InfoGroup {
    title: string;
    items: string[];
}

export const informationWeWorkWith: InfoGroup[] = [
    {
        title: "Complex Financial Data",
        items: [
            "Financial Statements",
            "Ledgers",
            "Bank Statements",
            "Bank Data",
            "Transaction Records",
            "Accounting Records",
        ],
    },
    {
        title: "Digital Evidence",
        items: [
            "Computers",
            "Mobile Devices",
            "Emails",
            "Digital Files",
            "Storage Media",
            "Electronic Communications",
        ],
    },
    {
        title: "Entity & Relationship Data",
        items: [
            "Companies",
            "Individuals",
            "Related Parties",
            "Ownership Structures",
            "Beneficial Interests",
        ],
    },
];

export const founder = {
    name: "Yash Saklecha",
    image: "/yash_sakchela.jpeg",
    role: "Director & Founder",
    credentials: "LL.B. | Certified Fraud Examiner (CFE)",
    experience: "10+ Years of Relevant Professional Experience",
    tags: "Forensic investigation • Financial intelligence • Digital-data review",
    bio: "Yash Saklecha is a forensic investigation and financial intelligence professional with over a decade of relevant experience across forensic investigation, financial analysis, digital-data review and complex investigative matters.",
    highlights: [
        "Financial investigations, fraud matters, whistleblower allegations, fund-flow and transaction analysis.",
        "Digital-data examination, due diligence, related-party analysis, asset tracing and investigative support.",
        "Corporate and business investigations involving financial irregularities, employee misconduct, procurement concerns, related-party transactions, fund diversion and other investigative concerns.",
        "Analytical support in matters involving government and enforcement authorities, including pre-search analysis, identification of relevant entities and transactions, financial and digital-data analysis, financial reconstruction and post-search analytical review.",
    ],
    disclaimer:
        "Certain experience may have been undertaken through prior professional roles or associations and is represented accordingly.",
};

export const coreAreas: string[] = [
    "Forensic Investigation",
    "Whistleblower Investigation",
    "Financial Intelligence",
    "Financial Analysis",
    "Fund-Flow Analysis",
    "Digital-Data Review",
    "Fraud Investigation",
    "Corporate Investigations",
    "Due Diligence",
    "Asset Tracing",
    "Transaction Analysis",
    "Investigation Support",
];

export interface Qualification {
    title: string;
    organisation?: string;
}

export const qualifications: Qualification[] = [
    { title: "LL.B." },
    {
        title: "Certified Fraud Examiner (CFE)",
        organisation: "Association of Certified Fraud Examiners",
    },
    { title: "B.Com", organisation: "University of Mumbai" },
];

export const professionalRecognition =
    "Professional and analytical work undertaken by our leadership has received multiple appreciation letters from Income Tax Investigation authorities, Government of India, during 2025–2026 in connection with investigative assignments. The recognition relates to work involving financial analysis, digital-data analysis, technical analysis, identification of financial discrepancies and investigative support in complex matters.";

export interface Discipline {
    title: string;
    description: string;
    icon: LucideIcon;
}

export const disciplines: Discipline[] = [
    {
        title: "Finance & Accounting",
        description: "Accounting, audit, financial reporting and financial analysis.",
        icon: Calculator,
    },
    {
        title: "Law",
        description: "Legal and regulatory understanding relevant to investigative matters.",
        icon: Scale,
    },
    {
        title: "Fraud Examination",
        description: "Fraud detection, prevention and investigation.",
        icon: SearchCheck,
    },
    {
        title: "Digital Forensics & Technology",
        description:
            "Technical expertise supporting digital evidence examination, data analysis and digital investigations.",
        icon: Fingerprint,
    },
    {
        title: "Forensic Science",
        description:
            "Specialised forensic expertise where the nature of an assignment requires technical examination.",
        icon: FlaskConical,
    },
    {
        title: "Business & Management",
        description: "Commercial, operational and business analysis.",
        icon: Briefcase,
    },
];

export interface ClientSegment {
    title: string;
    description: string;
    icon: LucideIcon;
}

export const clientSegments: ClientSegment[] = [
    {
        title: "Government & Enforcement Agencies",
        description:
            "Financial analysis, digital-data review, investigative analytics and specialist support, subject to engagement scope and applicable confidentiality requirements.",
        icon: Landmark,
    },
    {
        title: "Corporate Organisations",
        description:
            "Fraud investigations, whistleblower investigations, internal investigations, financial analysis, fraud risk assessment and due diligence.",
        icon: Building2,
    },
    {
        title: "Law Firms",
        description:
            "Financial investigation, bank-statement and transaction analysis, digital evidence analysis, asset tracing and investigative support.",
        icon: Scale,
    },
    {
        title: "Financial Institutions",
        description: "Fraud investigation, transaction analysis, financial intelligence and tracing.",
        icon: Banknote,
    },
    {
        title: "Insurance Companies",
        description:
            "Fraud analysis, financial investigation, document and transaction review and investigative support.",
        icon: Umbrella,
    },
    {
        title: "Private Equity & Venture Capital",
        description:
            "Investigative due diligence, financial analysis and integrity-risk assessment.",
        icon: TrendingUp,
    },
    {
        title: "Startups & Growing Businesses",
        description:
            "Fraud prevention, whistleblower response, control assessment, due diligence and investigative support.",
        icon: Rocket,
    },
];

export const experienceList: string[] = [
    "Financial investigations",
    "Whistleblower investigations",
    "Bank statement & financial document examination",
    "Corporate fraud investigations",
    "Digital-data analysis",
    "Fund & asset tracing",
    "Procurement & bid-related investigations",
    "Due diligence & investigative intelligence",
    "Search-related investigative support",
];

export const searchSupport = {
    title: "Search-Related Investigative Support",
    body: "Professional experience includes financial and digital-data analysis supporting pre-search analytical review, identification of relevant entities and transactions, investigative analysis and post-search review in matters involving government and enforcement authorities.",
    disclaimer:
        "Where professional experience arose through an association or another professional entity, it is represented accordingly and is not presented as a direct Infrasic Intelligence engagement.",
};

export const experienceScale = {
    heading: "From individual transactions to complex investigations.",
    body: "Our work spans matters ranging from individual transaction reviews to complex investigations involving multiple entities, individuals, transactions, financial records and digital sources.",
    stats: [
        {
            value: "200+*",
            label: "Investigative & analytical matters supported across relevant professional experience",
        },
        { value: "10+", label: "Years of relevant professional experience" },
        { value: "10", label: "Core practice areas" },
    ],
    footnote:
        "*Includes relevant professional experience of the founder and associated professionals prior to Infrasic Intelligence, where applicable.",
};

export interface Recognition {
    title: string;
    description: string;
    meta?: string;
}

export const recognitions: Recognition[] = [
    {
        title: "DPIIT Startup Recognition",
        description: "Recognised as a startup by DPIIT, Government of India.",
        meta: "Certificate DIPP283821 • Issued 19 September 2026 • Valid up to 22 July 2035.",
    },
    {
        title: "Udyam Registration",
        description: "Udyam Registration No. UDYAM-MH-18-0494358.",
    },
    {
        title: "Certified Fraud Examiner (CFE)",
        description:
            "Yash Saklecha holds the Certified Fraud Examiner (CFE) credential from the Association of Certified Fraud Examiners.",
        meta: "Credential granted 16 June 2020.",
    },
    {
        title: "Professional Recognition",
        description:
            "Seven appreciation letters (2025–2026) from Income Tax Investigation authorities recognise professional work involving financial, digital-data and technical analysis and investigative support.",
    },
];

export interface Certificate {
    id: string;
    title: string;
    issuer: string;
    holder: string;
    image: string;
    meta: string;
    kind: string;
}

export const certificates: Certificate[] = [
    {
        id: "dpiit",
        title: "Certificate of Recognition",
        issuer: "Department for Promotion of Industry & Internal Trade, Government of India",
        holder: "Infrasic Intelligence (OPC) Private Limited",
        image: "/certificates/dpiit-recognition.png",
        meta: "Certificate No. DIPP283821 • Issued 19 Sep 2026 • Valid up to 22 Jul 2035",
        kind: "DPIIT Startup India",
    },
    {
        id: "cfe",
        title: "Certified Fraud Examiner (CFE)",
        issuer: "Association of Certified Fraud Examiners",
        holder: "Yash Saklecha",
        image: "/certificates/cfe-yash-saklecha.png",
        meta: "Credential granted 16 June 2020",
        kind: "Professional Credential",
    },
];

export const credentialChips = [
    { label: "DPIIT Startup Recognition", value: "DIPP283821" },
    { label: "Udyam Registration", value: "UDYAM-MH-18-0494358" },
    { label: "Certified Fraud Examiner", value: "ACFE • 2020" },
    { label: "Income Tax Appreciation Letters", value: "7 • 2025–2026" },
];

export const contact = {
    firm: "INFRASIC INTELLIGENCE (OPC) PRIVATE LIMITED",
    founder: "Yash Saklecha",
    image: "/yash_sakchela.jpeg",
    role: "Director & Founder",
    credentials: "LL.B. | Certified Fraud Examiner (CFE)",
    phone: "+91 76111 81818",
    phoneHref: "tel:+917611181818",
    email: "contactus@infrasic.in",
    address:
        "Mumbai, Maharashtra",
    linkedin: "https://www.linkedin.com/company/126884187/",
    dpiit: "DIPP283821",
    udyam: "UDYAM-MH-18-0494358",
};
